import { useState } from 'react'
import MainScreenLab from '../Screens/MainScreenLab'
import OrdersScreen from '../Screens/OrdersScreen'
import ProfileScreenLab from '../Screens/ProfileScreenLab'
import PurchasesScreen, { PurchasedOrder } from '../Screens/PurchasesScreen'
import PaymentScreen from '../Screens/PaymentScreen'
import LabAdminPanelV1 from './LabAdminPanelV1'
import LabAdminPanelV3 from './LabAdminPanelV3'
import BottomNavLab from '../BottomNavLab'
import { ChevronLeftIcon } from '../Icons'
import { Product } from '../../data/products'
import { notifyOrderStatus, notifyPayment, getCurrentUserId } from '../../api/notifications'
import './DesignLab.css'

interface DesignLabProps {
  onClose: () => void
  onLog: (message: string) => void
}

type AppVersion = 'v1' | 'v3'
type TabScreen = 'home' | 'orders' | 'profile' | 'admin' | 'payment' | 'purchases'

interface LabOrder {
  id: string
  items: Product[]
  total: number
  status: 'pending' | 'confirmed' | 'cancelled' | 'paid'
  createdAt: string
  customerName: string
}

export default function DesignLab({ onClose, onLog }: DesignLabProps) {
  const [currentVersion, setCurrentVersion] = useState<AppVersion>('v1')
  const [currentTab, setCurrentTab] = useState<TabScreen>('home')
  const [cart, setCart] = useState<Product[]>([])
  const [currentPaymentOrderId, setCurrentPaymentOrderId] = useState<string | null>(null)
  
  // Оплаченные покупки (для экрана "Мои покупки")
  const [purchases, setPurchases] = useState<PurchasedOrder[]>([])
  
  // Shared orders для всех версий
  const [labOrders, setLabOrders] = useState<LabOrder[]>([])

  const versions = [
    { id: 'v1' as AppVersion, label: 'V1', description: 'Классические карточки' },
    { id: 'v3' as AppVersion, label: 'V3', description: 'Альтернативный дизайн' },
  ]

  const handleVersionChange = (versionId: AppVersion) => {
    setCurrentVersion(versionId)
    setCurrentTab('home')
    onLog(`Переключено на: ${versions.find(v => v.id === versionId)?.label}`)
  }

  const handleClose = () => {
    onClose()
    onLog('← Возврат из лаборатории')
  }

  const handleCreateOrder = () => {
    if (cart.length === 0) return
    
    const total = cart.reduce((sum, item) => {
      const priceStr = (item.price || '0').replace(/[^\d]/g, '')
      return sum + (parseInt(priceStr) || 0)
    }, 0)
    
    const newOrder: LabOrder = {
      id: `#LAB${String(labOrders.length + 1).padStart(3, '0')}`,
      items: [...cart],
      total,
      status: 'pending',
      createdAt: new Date().toISOString(),
      customerName: 'Тестовый клиент'
    }
    
    setLabOrders(prev => [...prev, newOrder])
    setCart([])
    setCurrentTab('orders')
    onLog(`[${currentVersion}] ✅ Заказ создан: ${newOrder.id}`)
    
    // Уведомление
    if ((window as any).Telegram?.WebApp) {
      (window as any).Telegram.WebApp.showAlert('Заказ создан! Ожидайте подтверждения от администратора.')
    }
  }

  // Удалено: handleConfirmOrder - подтверждение не требуется

  const handleCancelOrder = async (orderId: string) => {
    // Обновляем статус локально
    const order = labOrders.find(o => o.id === orderId)
    if (!order) return

    setLabOrders(prev => prev.map(o => 
      o.id === orderId ? { ...o, status: 'cancelled' as const } : o
    ))
    onLog(`[${currentVersion}] ❌ Заказ ${orderId} отменён`)
    
    // Отправляем уведомление через бота
    const userId = getCurrentUserId()
    if (userId) {
      const success = await notifyOrderStatus({
        user_id: userId,
        order_id: orderId,
        total: order.total,
        items_count: order.items.length,
        status: 'cancelled'
      })
      
      if (success) {
        onLog(`[${currentVersion}] 📨 Уведомление об отмене отправлено`)
      }
    }
  }

  const handlePayOrder = (orderId: string) => {
    setCurrentPaymentOrderId(orderId)
    setCurrentTab('payment')
    onLog(`[${currentVersion}] 💳 Переход к оплате ${orderId}`)
  }

  const handlePaymentConfirm = async () => {
    // Находим заказ который оплачиваем
    const orderToPay = labOrders.find(o => o.id === currentPaymentOrderId)
    if (orderToPay) {
      // Удаляем заказ из списка (переносим в purchases)
      setLabOrders(prev => prev.filter(order => order.id !== orderToPay.id))
      
      // Добавляем в покупки
      const newPurchase: PurchasedOrder = {
        id: orderToPay.id,
        items: orderToPay.items,
        total: orderToPay.total,
        purchasedAt: new Date().toISOString(),
        status: 'completed',
      }
      setPurchases(prev => [newPurchase, ...prev])
      
      onLog(`[${currentVersion}] ✅ Заказ ${orderToPay.id} оплачен и добавлен в покупки`)
      
      // Отправляем уведомление об оплате
      const userId = getCurrentUserId()
      if (userId) {
        const success = await notifyPayment({
          user_id: userId,
          order_id: orderToPay.id,
          total: orderToPay.total,
          items_count: orderToPay.items.length,
          status: 'paid'
        })
        
        if (success) {
          onLog(`[${currentVersion}] 📨 Уведомление об оплате отправлено`)
        } else {
          onLog(`[${currentVersion}] ⚠️ Не удалось отправить уведомление об оплате`)
        }
      }
      
      // Показываем уведомление
      if ((window as any).Telegram?.WebApp) {
        (window as any).Telegram.WebApp.showAlert('✅ Оплата прошла успешно! Заказ добавлен в "Мои покупки"')
      }
    }
    setCurrentPaymentOrderId(null)
    setCurrentTab('purchases') // Переходим на экран покупок
  }

  const handleRemoveFromCart = (index: number) => {
    setCart(prev => prev.filter((_, i) => i !== index))
    onLog(`[${currentVersion}] Удалён из корзины`)
  }

  // Добавление товара в заказы (НЕ сразу на оплату)
  const handleBuyNow = async (product: Product) => {
    const priceStr = (product.price || '0').replace(/[^\d]/g, '')
    const total = parseInt(priceStr) || 0
    
    const newOrder: LabOrder = {
      id: `#LAB${String(labOrders.length + 1).padStart(3, '0')}`,
      items: [product],
      total,
      status: 'pending',
      createdAt: new Date().toISOString(),
      customerName: 'Пользователь'
    }
    
    setLabOrders(prev => [...prev, newOrder])
    setCurrentTab('orders')
    onLog(`[${currentVersion}] 🛒 Товар добавлен в заказы: ${product.title}`)
    
    // Отправляем уведомление в Telegram
    const userId = getCurrentUserId()
    if (userId) {
      const success = await notifyOrderStatus({
        user_id: userId,
        order_id: newOrder.id,
        total: newOrder.total,
        items_count: newOrder.items.length,
        status: 'pending'
      })
      
      if (success) {
        onLog(`[${currentVersion}] 📨 Уведомление о заказе отправлено в Telegram`)
      }
    }
    
    // Telegram WebApp уведомление
    if ((window as any).Telegram?.WebApp) {
      (window as any).Telegram.WebApp.showAlert('✅ Товар добавлен в "Мои заказы"! Оплатите заказ во вкладке "Заказы"')
    }
  }

  const handleTabNavigate = (tab: 'home' | 'orders' | 'profile' | 'purchases') => {
    setCurrentTab(tab)
    onLog(`[${currentVersion}] Переход: ${tab}`)
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', paddingBottom: '80px' }}>
      {/* Шапка с версиями */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#141418',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        {/* Верхняя строка - назад + админ */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              padding: '8px 14px',
              color: '#fff',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <ChevronLeftIcon size={14} />
            Назад
          </button>

          <div style={{
            flex: 1,
            textAlign: 'center',
          }}>
            <div style={{
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
            }}>
              🧪 Лаборатория дизайна
            </div>
          </div>

          <button
            onClick={() => setCurrentTab(currentTab === 'admin' ? 'home' : 'admin')}
            style={{
              background: currentTab === 'admin' ? 'rgba(147, 51, 234, 0.3)' : 'transparent',
              border: '1px solid rgba(147, 51, 234, 0.5)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#a855f7',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            👨‍💼
          </button>
        </div>

        {/* Переключатель версий */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '12px 16px',
        }}>
          {versions.map(version => (
            <button
              key={version.id}
              onClick={() => handleVersionChange(version.id)}
              style={{
                flex: 1,
                padding: '10px 8px',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '8px',
                border: currentVersion === version.id 
                  ? 'none'
                  : '1px solid rgba(255, 255, 255, 0.12)',
                background: currentVersion === version.id
                  ? 'linear-gradient(135deg, #9333ea, #a855f7)'
                  : 'transparent',
                color: currentVersion === version.id ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div>{version.label}</div>
              <div style={{
                fontSize: '10px',
                opacity: currentVersion === version.id ? 1 : 0.5,
                marginTop: '2px',
              }}>
                {version.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Контент - текущая версия приложения */}
      <div>
        {/* Главный экран */}
        {currentTab === 'home' && (
          <MainScreenLab 
            onLog={onLog} 
            onAddToCart={handleBuyNow}
            version={currentVersion}
          />
        )}

        {/* Заказы */}
        {currentTab === 'orders' && (
          <OrdersScreen 
            onLog={onLog} 
            orders={cart}
            labOrders={labOrders}
            onRemoveOrder={handleRemoveFromCart}
            onCreateOrder={handleCreateOrder}
            onPayOrder={handlePayOrder}
          />
        )}

        {/* Оплата */}
        {currentTab === 'payment' && currentPaymentOrderId && (
          <PaymentScreen 
            onConfirm={handlePaymentConfirm}
            onBack={() => {
              setCurrentPaymentOrderId(null)
              setCurrentTab('orders')
            }}
            totalAmount={labOrders.find(o => o.id === currentPaymentOrderId)?.total || 0}
            itemsCount={labOrders.find(o => o.id === currentPaymentOrderId)?.items.length || 0}
          />
        )}

        {/* Профиль */}
        {currentTab === 'profile' && (
          <ProfileScreenLab 
            orders={cart}
            version={currentVersion}
          />
        )}

        {/* Мои покупки */}
        {currentTab === 'purchases' && (
          <PurchasesScreen 
            purchases={purchases}
            version={currentVersion}
          />
        )}

        {/* Админ панель - разная для каждой версии */}
        {currentTab === 'admin' && currentVersion === 'v1' && (
          <LabAdminPanelV1 
            onLog={onLog}
            orders={labOrders}
            onConfirmOrder={() => {}}
            onCancelOrder={handleCancelOrder}
          />
        )}
        {currentTab === 'admin' && currentVersion === 'v3' && (
          <LabAdminPanelV3 
            onLog={onLog}
            orders={labOrders}
            onConfirmOrder={() => {}}
            onCancelOrder={handleCancelOrder}
          />
        )}
      </div>

      {/* Нижняя навигация - только если НЕ админ панель и НЕ оплата */}
      {currentTab !== 'admin' && currentTab !== 'payment' && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
        }}>
          <BottomNavLab 
            activeTab={currentTab}
            onNavigate={handleTabNavigate}
          />
        </div>
      )}
    </div>
  )
}
